import {
    type FC,
    useMemo,
    useState,
    useEffect,
    useContext,
    useCallback,
    createContext,
    type ReactNode,
} from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers';
import { getBuyerId, getUserType, getPriceBookName } from 'dibs-cookie-jar';
import { setPdpUserRegionalInfo } from '../../helpers/setPdpUserRegionalInfo';
import { CUSTOMER_TYPE_TRADE } from 'dibs-buyer-layout/exports/userStorageConstants';
import { useDblExternalSelector } from 'dibs-buyer-layout/exports/DblExternalContext';
import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers';

// types
import { type pdpRespMarketplaceSecondaryRootQuery$variables as RelayVariables } from '../../../queries/__generated__/pdpRespMarketplaceSecondaryRootQuery.graphql';
import { type PdpRespMarketplaceLayoutSecondaryContextProvider_viewer$key as Viewer } from './__generated__/PdpRespMarketplaceLayoutSecondaryContextProvider_viewer.graphql';

type Refetch = (vars: $TSFixMe, options: { onComplete: (error: Error | null) => void }) => void;

type PdpRespMarketplaceLayoutSecondaryContextType = {
    currency: string;
    relayVars: RelayVariables;
};

const relayDefaults = {
    userId: '',
    itemId: '',
    itemIds: [''],
    isTrade: false,
    fetchUser: false,
    afterDisplay: false,
};

const PdpRespMarketplaceLayoutSecondaryContext =
    createContext<PdpRespMarketplaceLayoutSecondaryContextType>({
        currency: '',
        relayVars: relayDefaults,
    });

const viewerFragment = graphql`
    fragment PdpRespMarketplaceLayoutSecondaryContextProvider_viewer on Viewer {
        itemRead(itemIds: $itemIds) {
            ... on Item {
                seller {
                    isAccessible
                }
            }
        }
    }
`;

export const PdpRespMarketplaceLayoutSecondaryContextProvider: FC<{
    viewer: Viewer;
    refetch: Refetch;
    children: ReactNode;
    relayVariables: RelayVariables;
}> = ({ children, refetch, relayVariables, viewer: viewerRef }) => {
    const environment = useRelayEnvironment();
    const viewer = useFragment(viewerFragment, viewerRef);
    const [relayVars, setRelayVars] = useState(relayVariables);
    const currency = useDblExternalSelector<$TSFixMe, string>(store => store.currency.currency);

    const handleRefetch = useCallback(async () => {
        const userId = getBuyerId(document.cookie) || '';
        const userType = getUserType(document.cookie);
        const isTrade = userType === CUSTOMER_TYPE_TRADE;
        const isInteractivePdpChat = viewer?.itemRead?.[0]?.seller?.isAccessible;

        await setPdpUserRegionalInfo({ userId, environment });

        const updatedRelayVars = {
            userId,
            isTrade,
            afterDisplay: true,
            fetchUser: !!userId,
            isInteractivePdpChat,
            userCountryCode: getUserSessionCountryCode(),
            priceBookName: getPriceBookName(document.cookie) || 'DEFAULT',
        };

        refetch?.(updatedRelayVars, {
            onComplete: error => {
                if (!error) {
                    setRelayVars((originalRelayVars: RelayVariables) => ({
                        ...originalRelayVars,
                        ...updatedRelayVars,
                    }));
                }
            },
        });
    }, [refetch, viewer, environment]);

    useEffect(() => {
        addLoginHandlers(() => {
            handleRefetch();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(
        () => ({
            currency,
            relayVars,
        }),
        [currency, relayVars]
    );
    return (
        <PdpRespMarketplaceLayoutSecondaryContext.Provider value={value}>
            {children}
        </PdpRespMarketplaceLayoutSecondaryContext.Provider>
    );
};

export const usePdpRespMarketplaceLayoutSecondaryContext =
    (): PdpRespMarketplaceLayoutSecondaryContextType => {
        return useContext(PdpRespMarketplaceLayoutSecondaryContext);
    };
